import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, rangeQuery, type MetricsResponse, type Site, type StatsResponse } from "../lib/api";
import { buildRange, type RangeKind } from "../lib/format";
import { RangePicker } from "../components/RangePicker";
import { OverviewContent } from "../components/OverviewContent";
import { AppShell } from "../components/AppShell";
import { ErrorBox, PageSpinner } from "../components/ui";

type SharedSite = Pick<Site, "id" | "name" | "domain" | "tz" | "tz_offset">;

export default function SharePage() {
  const { token = "" } = useParams();
  const [rangeKind, setRangeKind] = useState<RangeKind>("24h");

  const siteQuery = useQuery({
    queryKey: ["share-site", token],
    queryFn: () => api.get<{ site: SharedSite }>(`/api/share/${token}`),
  });

  const site = siteQuery.data?.site;
  const nowRounded = Math.floor(Date.now() / 60000) * 60000;
  const range = useMemo(
    () => buildRange(rangeKind, nowRounded, site?.tz_offset ?? 0),
    [rangeKind, nowRounded, site?.tz_offset],
  );

  const stats = useQuery({
    queryKey: ["share-stats", token, range],
    queryFn: () => api.get<StatsResponse>(`/api/share/${token}/stats${rangeQuery(range)}`),
    enabled: Boolean(site),
  });

  const metrics = useQuery({
    queryKey: ["share-metrics", token, range],
    queryFn: () => api.get<MetricsResponse>(`/api/share/${token}/metrics${rangeQuery(range, 10)}`),
    enabled: Boolean(site),
  });

  return (
    <AppShell
      extra={
        site && (
          <>
            <span className="truncate text-sm font-semibold text-slate-800">{site.name}</span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
              Dibagikan
            </span>
          </>
        )
      }
      right={<RangePicker kind={rangeKind} onChange={setRangeKind} />}
    >
      {siteQuery.isLoading ? (
        <PageSpinner />
      ) : !site ? (
        <ErrorBox message="Tautan tidak valid atau sudah dinonaktifkan." />
      ) : stats.isLoading || metrics.isLoading ? (
        <PageSpinner />
      ) : stats.error || metrics.error || !stats.data || !metrics.data ? (
        <ErrorBox message="Gagal memuat statistik." />
      ) : (
        <OverviewContent stats={stats.data} metrics={metrics.data} range={range} tz={site.tz} />
      )}
    </AppShell>
  );
}
